/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from 'react';
import { ExpandMoreRounded, Search } from '@mui/icons-material';
import {
  Box, Divider, Grid, MenuItem, Pagination, Select, Skeleton, TextField,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import Images from '../../../../Configs/images';
import Elements from '../../../Elements';
import ROUTES from '../../../../Configs/routes';
import { getMarketItems } from '../../../../Redux/Models/market';
import AlertContext from '../../../../Context/AlertContext';
import { formatNumToCurrency } from '../../../../Utils/helpers';

export default function Market({
  data, classes, error, message, isLoading, totalPage = 1, currentPage = 1, toSelect,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('Newest');
  const [activeTab, setActiveTab] = useState('item');
  const [saleType, setSaleType] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [column, setColumn] = useState(2);

  const { openAlert } = useContext(AlertContext);

  const getData = () => {
    dispatch(getMarketItems('', {
      limit, page, keyword, saleType: saleType === 'all' ? '' : saleType, sort, activeTab,
    }));
  };

  useEffect(() => {
    getData();
  }, [limit, page, saleType, sort, activeTab]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (page === 1) {
        getData();
      } else {
        setPage(1);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [keyword]);

  useEffect(() => {
    if (error) {
      openAlert({
        message: `Something went wrong. ${message}`,
        severity: 'error',
      });
    }
  }, [error]);

  const handleClickItem = (item) => {
    if (toSelect) {
      toSelect(item);
    } else {
      navigate(`${ROUTES.market()}/detail/${item.id}?type=${activeTab}`);
    }
  };

  const renderItems = () => {
    if (isLoading) {
      return (
        <Grid container spacing={2}>
          {new Array(3).fill(0).map((l, idx) => (
            <Grid item xs={12 / column} key={idx}>
              <Box display="flex" gap={1.5} border="1px solid #E5E5E5" borderRadius="5px" p={2}>
                <Skeleton variant="rectangular" height={80} width={80} />
                <Box flex={1}>
                  <Box className="weight-600">
                    <Skeleton variant="text" width="80%" />
                  </Box>
                  <Box display="flex" className="s-10" color="#586479" gap={0.5} flexWrap="wrap" alignItems="center" mt={0.25}>
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="80%" />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                    <Box className="s-16 weight-700">
                      <Skeleton variant="text" width="50%" />
                    </Box>
                    <Box display="flex" gap={0.5}>
                      <Skeleton variant="text" width="20%" />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      );
    }
    if (data.length) {
      return (
        <Grid container spacing={2}>
          { data.map((el, index) => {
            const item = el.item || el.box;
            return (
              <Grid item xs={12 / column} key={item.id}>
                <Box display="flex" gap={1.5} onClick={() => handleClickItem(item)} key={index} border="1px solid #E5E5E5" borderRadius="5px" p={2}>
                  <img
                    width="80px"
                    height="80px"
                    className="rounded cover"
                    src={item.photo}
                    alt="baco"
                  />
                  <Box flex={1}>
                    <Box className="weight-600">{item.name}</Box>
                    <Box display="flex" className="s-10" color="#586479" gap={0.5} flexWrap="wrap" alignItems="center" mt={0.25}>
                      {
                        item.category_name && (<div>{item.category_name || '-'}</div>)
                      }
                      {
                        item.condition_name && (
                        <div>
                          ・
                          {item.condition_name || '-'}
                          ・
                        </div>
                        )
                      }
                      <div>
                        {item.volume || '0 mL'}
                      </div>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                      <Box className="s-16 weight-700">
                        {formatNumToCurrency(Number(item.price))}
                      </Box>
                      <Box display="flex" gap={0.5}>
                        <Box fontSize={12}>Qty 1</Box>
                        <img src={Images.CheckRed} alt="check-icon" />
                      </Box>
                    </Box>
                    {item.sale_type === 'Bid Price'
                    && (
                    <Box className="s-12" mt={0.5}>
                      {item.bidder_count}
                      {' '}
                      bids・
                      {moment(item.end_date).format('DD MMM YYYY (HH.mm)')}
                    </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      );
    }

    return (
      <div className="s-16 mt-5 center">
        <img src={Images.Sure} alt="No data" />
        <div className="weight-700 mt-4">Market items will appear in here soon</div>
        <div className="mt-3">Check again later</div>
      </div>
    );
  };

  return (
    <Elements.AdminPageBase navigate={navigate}>
      <div className={classes.root}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box fontSize={20}>
            <strong>OenoMarket</strong>
          </Box>
          <Box>
            <Link to="/" className="primary-green italic s-16">See Live View</Link>
          </Box>
        </Box>
        <Box mt={2} className="flex gap-16 space-between">
          <Box display="flex" gap={1}>
            <Select
              select
              size="small"
              value={saleType}
              onChange={({ target }) => setSaleType(target.value)}
              IconComponent={ExpandMoreRounded}
              sx={{
                borderRadius: '10px',
                bgcolor: '#fff',
              }}
            >
              <MenuItem value="all">All Type</MenuItem>
              <MenuItem value="Fixed Price">Fixed Price</MenuItem>
              <MenuItem value="Bid Price">Bid Price</MenuItem>
            </Select>
            <Select
              select
              size="small"
              value={activeTab}
              onChange={({ target }) => setActiveTab(target.value)}
              IconComponent={ExpandMoreRounded}
              sx={{
                borderRadius: '10px',
                bgcolor: '#fff',
              }}
            >
              <MenuItem value="item">Item</MenuItem>
              <MenuItem value="box">Box</MenuItem>
            </Select>
          </Box>
          <Box display="flex" gap={2}>
            <Select
              select
              size="small"
              value={sort}
              onChange={({ target }) => setSort(target.value)}
              IconComponent={ExpandMoreRounded}
              sx={{
                borderRadius: '10px',
                bgcolor: '#fff',
              }}
            >
              <MenuItem value="Oldest">Oldest</MenuItem>
              <MenuItem value="Newest">Newest</MenuItem>
            </Select>
            <Select
              select
              size="small"
              value={limit}
              onChange={({ target }) => setLimit(target.value)}
              IconComponent={ExpandMoreRounded}
              sx={{
                borderRadius: '10px',
                bgcolor: '#fff',
              }}
            >
              <MenuItem value={10}>10 List</MenuItem>
              <MenuItem value={20}>20 List</MenuItem>
              <MenuItem value={50}>50 List</MenuItem>
            </Select>
            <TextField
              placeholder="Search by keyword"
              size="small"
              sx={{
                ' .MuiInputBase-root': {
                  bgcolor: '#fff',
                },
              }}
              value={keyword}
              onChange={({ target }) => setKeyword(target.value)}
              InputProps={{
                startAdornment: (<Search fontSize="small" sx={{ mr: 1, color: '#D4D4D4' }} />),
              }}
            />
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Box fontSize={12} color="#586479">
            <strong>{data.length}</strong>
            {' '}
            items
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Pagination
              count={totalPage}
              variant="outlined"
              shape="rounded"
              size="small"
              page={!isLoading ? currentPage : page}
              siblingCount={0}
              onChange={(e, newPage) => setPage(newPage)}
            />
            <Divider orientation="vertical" flexItem />
            <Box display="flex" alignItems="center" gap={1}>
              {[2, 3].map((val) => (
                <Box
                  key={val}
                  border={val !== column ? '1px solid #E5E5E5' : ''}
                  bgcolor={val === column ? '#38AC3D' : 'none'}
                  boxSizing="border-box"
                  borderRadius="5px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={24}
                  height={24}
                  color={val === column ? '#fff' : '#000'}
                  fontSize={12}
                  className="link"
                  onClick={() => setColumn(val)}
                >
                  {val}
                </Box>
              ))}
              <Box color="#404852" fontWeight="600" fontSize={14}>Columns</Box>
            </Box>
          </Box>
        </Box>

        <Box mt={2} bgcolor="#FDFDFD" borderRadius="10px" p={2}>
          {renderItems()}
        </Box>
      </div>
    </Elements.AdminPageBase>
  );
}
